import { useState, useCallback } from 'react';
import { useFetch } from 'app/queryProvider';
import { IPaginatedResponse } from '@shared/types/pagination';
import { usePaginatedData } from './usePaginatedData';
import { IResource } from 'app/queryProvider/interfaces';

interface UsePaginatedQueryOptions<T> {
    url: string;
    queryKey: [string[]];
    resource: IResource;
    enabled?: boolean;
    initialPage?: number;
    pageSize?: number;
}
export function usePaginatedQuery<T>(options: UsePaginatedQueryOptions<T>) {
    const [currentPage, setCurrentPage] = useState(options.initialPage || 1);
    const paginatedUrl = `${options.url}?page=${currentPage}${options.pageSize ? `&page_size=${options.pageSize}` : ''
        }`;

    const {
        data: response,
        isFetching,
        error,
        isError
    } = useFetch<IPaginatedResponse<T>>({
        url: paginatedUrl,
        queryKey: [options.queryKey[0].concat([currentPage.toString()])],
        resource: options.resource,
        options: {
            enabled: options.enabled
        }
    });

    const paginatedData = usePaginatedData(response);

    const goToNextPage = useCallback(() => {
        if (paginatedData.meta?.hasNext) {
            setCurrentPage(prev => prev + 1);
        }
    }, [paginatedData.meta?.hasNext]);

    const goToPreviousPage = useCallback(() => {
        if (paginatedData.meta?.hasPrevious) {
            setCurrentPage(prev => prev - 1);
        }
    }, [paginatedData.meta?.hasPrevious]);

    const goToPage = useCallback((page: number) => {
        setCurrentPage(page);
    }, []);

    return {
        ...paginatedData,
        isLoading: isFetching,
        error,
        isError,
        currentPage,
        goToNextPage,
        goToPreviousPage,
        goToPage,
        canGoNext: paginatedData.meta?.hasNext || false,
        canGoPrevious: paginatedData.meta?.hasPrevious || false,
    };
}
